import { g, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { ShapeSelectionHandler } from './handlers/shape-selection-handler';
import { PolySelectionHandler } from './handlers/poly-selection-handler';
import { DrawingToolsHandler } from './handlers/drawing-tools-handler';
import { VectorToolsHandler } from './handlers/vector-tools-handler';
import { KeyboardHandler } from './handlers/keyboard-handler';
import { FinishHandler } from './handlers/finish-handler';
import { SecondaryToolsHandler } from './handlers/secondary-tools-handler';
import { getActiveLayer } from './layers';
import { deselect } from '@hcie/tools';
import { applyZoom, zoomIn, zoomOut } from './zoom';

/**
 * Event Dispatcher Proxy
 * Coordinates all manual canvas events by delegating to specialized handlers.
 */

function updateCoordinates(e: MouseEvent) {
    const canvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    
    // Account for internal vs screen resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // `rect` already reflects the current CSS size (including zoom). Using scaleX/scaleY
    // converts viewport pixels to document pixels. Do NOT divide by g.zoom again.
    g.pX = (e.clientX - rect.left) * scaleX;
    g.pY = (e.clientY - rect.top) * scaleY;
}

export function on_canvas_mouse_down(e: MouseEvent) {
    updateCoordinates(e);
    g.startX = g.pX;
    g.startY = g.pY;
    
    console.log(`[DEBUG 2026-04-07][TaskID: Stabilize] MouseDown at ${Math.round(g.pX)}, ${Math.round(g.pY)} (Tool: ${g.current_tool.id})`);

    // Backup current layer state for Undo/Redo before starting any tool action
    const activeLayer = getActiveLayer();
    if (activeLayer && activeLayer.ctx) {
        g.previousLayerState = activeLayer.ctx.getImageData(0, 0, g.image_width, g.image_height);
    }

    // Alt key logic for quick Eyedropper (if not already selecting)
    if (e.altKey && g.current_tool.id !== Tool.Lasso.id && g.current_tool.id !== Tool.PolySelect.id) {
        if (SecondaryToolsHandler.handleMouseDown(e)) return;
    }

    if (ShapeSelectionHandler.handleMouseDown(e)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', e);
        return;
    }
    if (PolySelectionHandler.handleMouseDown(e)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', e);
        return;
    }
    if (SecondaryToolsHandler.handleMouseDown(e)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', e);
        return;
    }
    if (DrawingToolsHandler.handleMouseDown(e)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', e);
        return;
    }
    if (VectorToolsHandler.handleMouseDown(e)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', e);
        return;
    }

    EventBus.emit('CANVAS_MOUSE_DOWN', e);
}

function updateCanvasCursor() {
    const canvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
    if (!canvas) return;

    const id = g.current_tool.id;
    const drawingTools = [Tool.Pen.id, Tool.Brush.id, Tool.Eraser.id, Tool.Spray.id, 'btn-red-eye'];
    
    // 1. If a drawing tool is active, hide browser cursor (layers.ts draws circle)
    if (drawingTools.includes(id)) {
        canvas.style.cursor = 'none';
        return;
    }

    // 2. Check for selection handle hovers
    const shapeCursor = ShapeSelectionHandler.getHoverCursor();
    if (shapeCursor) {
        canvas.style.cursor = shapeCursor;
        return;
    }

    const polyCursor = PolySelectionHandler.getHoverCursor();
    if (polyCursor) {
        canvas.style.cursor = polyCursor;
        return;
    }

    // 3. Defaults based on tool category
    const selectionTools = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-lasso', 'btn-poly-select', 'btn-move-selection', 'btn-move-content'
    ];
    
    if (selectionTools.includes(id)) {
        canvas.style.cursor = 'crosshair';
    } else if (id === Tool.Eye_Dropper.id) {
        canvas.style.cursor = 'cell';
    } else {
        canvas.style.cursor = 'default';
    }
}

export function on_canvas_mouse_move(e: MouseEvent) {
    updateCoordinates(e);

    if (ShapeSelectionHandler.handleMouseMove(e)) {
        updateCanvasCursor();
        // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] Real-time render during handle drag
        EventBus.emit('RENDER_LAYERS', undefined);
        EventBus.emit('CANVAS_MOUSE_MOVE', e);
        return;
    }
    if (PolySelectionHandler.handleMouseMove(e)) {
        updateCanvasCursor();
        // Real-time render during poly/lasso drag
        EventBus.emit('RENDER_LAYERS', undefined);
        EventBus.emit('CANVAS_MOUSE_MOVE', e);
        return;
    }
    if (DrawingToolsHandler.handleMouseMove(e)) {
        updateCanvasCursor();
        EventBus.emit('CANVAS_MOUSE_MOVE', e);
        return;
    }
    
    // Always trigger render for hover preview cursors if a drawing tool is active
    const drawingTools = [Tool.Pen.id, Tool.Brush.id, Tool.Eraser.id, Tool.Spray.id, 'btn-red-eye'];
    if (drawingTools.includes(g.current_tool.id)) {
        (g as any).needsRender = true;
    }

    if (VectorToolsHandler.handleMouseMove(e)) {
        updateCanvasCursor();
        EventBus.emit('CANVAS_MOUSE_MOVE', e);
        return;
    }

    updateCanvasCursor();
    EventBus.emit('CANVAS_MOUSE_MOVE', e);
}

export function on_canvas_mouse_up(e: MouseEvent) {
    if (ShapeSelectionHandler.handleMouseUp(e)) {
        updateCanvasCursor();
        return;
    }
    if (PolySelectionHandler.handleMouseUp(e)) {
        updateCanvasCursor();
        return;
    }
    if (FinishHandler.finishDrawing(e)) {
        updateCanvasCursor();
        return;
    }
}

export function on_canvas_dblclick(e: MouseEvent) {
    if (PolySelectionHandler.handleDoubleClick(e)) {
        updateCanvasCursor();
        return;
    }
}

export function attachCanvasEvents(can: HTMLCanvasElement) {
    if (!can) return;
    can.onmousedown = on_canvas_mouse_down;
    can.onmousemove = on_canvas_mouse_move;
    can.onmouseup = on_canvas_mouse_up;
    can.ondblclick = on_canvas_dblclick;

    // Wheel zoom (requested behavior): zoom in/out on mouse wheel while cursor is over canvas area.
    // Use non-passive listener so we can prevent page/area scrolling when zooming.
    const scrollArea = document.getElementById('canvasScrollArea') as HTMLElement | null;
    const target = scrollArea || can;
    const key = '__hcie_canvas_wheel_zoom_attached__';
    if (!(window as any)[key]) {
        (window as any)[key] = true;
        target.addEventListener('wheel', (e: WheelEvent) => {
            // If user is scrolling elsewhere, ignore.
            const t = e.target as HTMLElement | null;
            if (!t) return;

            // Do not interfere with range inputs or number inputs (e.g., status bar controls).
            const tag = t.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

            // Normalize wheel direction: deltaY < 0 = zoom in, deltaY > 0 = zoom out.
            // Prevent default so the scroll area doesn't pan while zooming.
            e.preventDefault();
            if (e.deltaY < 0) zoomIn(e);
            else if (e.deltaY > 0) zoomOut(e);
        }, { passive: false });
    }
}

// --- Restored Functions ---

export function selectTool(tool: Tool) {
    if (!tool) return;
    
    // TOOL CLEANUP: If switching AWAY from a specific selection tool or to a different one,
    // clear lingering handles and commit edit mode to prevent handle overlapping.
    const selectionTools = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-lasso', 'btn-poly-select', 'btn-single-row-select', 'btn-single-column-select'
    ];

    const wasSelection = selectionTools.includes(g.current_tool.id);
    const isChangingTool = g.current_tool.id !== tool.id;

    // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] Always clear handles on tool switch
    if (wasSelection && isChangingTool) {
        (g as any)._selectionEditBounds = null;
        (g as any)._draggingCornerIndex = -1;
        (g as any)._draggingPointIndex = -1;
        console.log(`[ToolCleanup] Cleared handles: ${g.current_tool.id} → ${tool.id}`);
    }

    if (wasSelection && isChangingTool && g.isSelectionActive && (g as any).settings?.selection?.ghostMode) {
        console.log(`[ToolCleanup] Committing ghost selection before switching.`);
        if (typeof (window as any).commitDrawing === 'function') {
            (window as any).commitDrawing();
        }
    }

    g.current_tool = tool;
    updateCanvasCursor();
    EventBus.emit('TOOL_CHANGED', { toolId: tool.id });
    
    if (typeof (window as any).updateOptionsBar === 'function') {
        (window as any).updateOptionsBar();
    }
}

export function undoImage() {
    if (window.historyManager) {
        window.historyManager.undo();
        EventBus.emit('RENDER_LAYERS');
    }
}

export function redoImage() {
    if (window.historyManager) {
        window.historyManager.redo();
        EventBus.emit('RENDER_LAYERS');
    }
}

export function clearSelected() {
    deselect();
}

// Global event listeners to prevent "sticky" tools when mouseup occurs outside canvas
if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (e) => KeyboardHandler.handleKeyDown(e));
    window.addEventListener('mouseup', (e) => {
        // Fallback: if we are drawing but mouseup happened on parent, finalize it
        if (g.drawing || g.movingSelection) on_canvas_mouse_up(e);
    });
}

// Legacy exposure for components not yet converted to new architecture
(window as any).finishDrawing = (e: MouseEvent | null) => FinishHandler.finishDrawing(e);
(window as any).attachCanvasEvents = attachCanvasEvents;
(window as any).selectTool = selectTool;
(window as any).undoImage = undoImage;
(window as any).redoImage = redoImage;
(window as any).zoomIn = (e: MouseEvent | null) => zoomIn(e);
(window as any).zoomOut = (e: MouseEvent | null) => zoomOut(e);
(window as any).applyZoom = (oldZoom: number, e: MouseEvent | null) => applyZoom(oldZoom, e);
